import { EXPERIENCE_CATEGORY_BY_SLUG } from '../../utils/experienceCategories';

/**
 * Create the DOM element for an experience map marker.
 * Smaller than a venue pin so experiences read as an "extra layer"
 * rather than competing with venues. Colored by the category palette
 * with the category glyph inside as inline SVG so it renders crisp at
 * any DPI. The `size` is driven by the smooth zoom-size effect in
 * `useGuestMapMarkers`; passing the current size at creation time
 * means a newly-added pin matches the current zoom's size on first
 * paint (no "pop" from the default to the correct size).
 */
// Fixed DOM size for experience marker outer element. We NEVER mutate
// this at runtime — zoom-driven resizing is applied as a CSS transform
// scale on the inner wrap so MapLibre's `center` anchor math stays
// perfectly stable across every zoom frame.
export const EXPERIENCE_MARKER_DOM_SIZE = 22;

export function createExperienceMarkerElement({ category, isSelected, sizeScale = 1, testId } = {}) {
  const cat = EXPERIENCE_CATEGORY_BY_SLUG[category] || null;
  const color = cat?.color || '#7C3AED';
  // Glyph is sized once at DOM creation. The whole wrap scales via
  // CSS transform, so the glyph scales with it — no need to resize
  // the SVG on each zoom tick, and no "icon disappears when zoomed
  // out" (user-reported) because the glyph is ALWAYS present.
  const glyphSize = Math.max(5, Math.round(EXPERIENCE_MARKER_DOM_SIZE * 0.55));
  const glyphSvg = glyphMarkup(cat?.glyph, glyphSize);

  const el = document.createElement('div');
  el.setAttribute('data-testid', testId || 'guest-map-experience-marker');
  el.style.cursor = 'pointer';
  el.style.width = `${EXPERIENCE_MARKER_DOM_SIZE}px`;
  el.style.height = `${EXPERIENCE_MARKER_DOM_SIZE}px`;
  el.style.transformOrigin = 'center';

  const wrap = document.createElement('div');
  wrap.setAttribute('data-marker-inner', '');
  wrap.style.width = `${EXPERIENCE_MARKER_DOM_SIZE}px`;
  wrap.style.height = `${EXPERIENCE_MARKER_DOM_SIZE}px`;
  wrap.style.borderRadius = '50%';
  wrap.style.background = color;
  wrap.style.border = '2px solid #FFFFFF';
  wrap.style.display = 'flex';
  wrap.style.alignItems = 'center';
  wrap.style.justifyContent = 'center';
  wrap.style.boxShadow = isSelected
    ? `0 0 0 4px ${color}44, 0 6px 14px rgba(8, 12, 24, 0.28)`
    : '0 4px 10px rgba(8, 12, 24, 0.28)';
  // CRITICAL: `transform-origin: center` + `scale()` ONLY on the inner
  // wrap means the DOM box never changes size, so MapLibre's `center`
  // anchor computation yields the same pixel offset every frame.
  wrap.style.transformOrigin = 'center center';
  const effectiveScale = (isSelected ? 1.16 : 1) * sizeScale;
  wrap.style.transform = `scale(${effectiveScale})`;
  wrap.style.transition = 'transform 160ms ease, box-shadow 200ms ease';
  wrap.dataset.sizeScale = String(sizeScale);
  wrap.dataset.selected = isSelected ? '1' : '0';
  wrap.innerHTML = glyphSvg;
  el.appendChild(wrap);
  return el;
}

function glyphMarkup(glyph, glyphSize = 12) {
  // Stroke-only SVGs to match ExperienceCategoryGlyph.jsx paths but
  // rendered directly to string for injection into a non-React DOM node.
  const common = `width="${glyphSize}" height="${glyphSize}" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"`;
  switch (glyph) {
    case 'wave':
      return `<svg ${common}><path d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/><path d="M2 18c2-3 4-3 6 0s4 3 6 0 4-3 6 0"/></svg>`;
    case 'mountain':
      return `<svg ${common}><path d="M3 20l6-10 4 6 3-4 5 8H3z"/><circle cx="16" cy="7" r="1.4"/></svg>`;
    case 'boot':
      return `<svg ${common}><path d="M6 4h4v8l6 2 4 1v4H4v-3l2-2z" stroke-linejoin="round"/><path d="M7 7h3M7 9.5h3"/></svg>`;
    case 'camera':
      return `<svg ${common}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7l2-3h4l2 3"/><circle cx="12" cy="13.5" r="3.5"/></svg>`;
    case 'column':
      return `<svg ${common}><path d="M4 6h16M4 20h16"/><path d="M7 6v14M12 6v14M17 6v14"/></svg>`;
    case 'target':
      return `<svg ${common}><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1" fill="#FFFFFF"/></svg>`;
    default:
      return `<svg ${common}><circle cx="12" cy="12" r="6"/></svg>`;
  }
}
